<script setup>
import { computed, ref } from 'vue';

/**
 * What grew and what shrank, package by package.
 *
 * The question the legacy Revenue Report structurally cannot answer: it reports one window at a
 * time and one dimension per run, so "what is dying" was a manual diff of two printouts.
 *
 * Ranked by money moved rather than by percent, because a 300% rise on a $400 line is noise
 * beside a 14% fall on a $115k one — and because a package with no prior sales has no percent
 * at all, only a first appearance.
 */
const props = defineProps({
  /** { risers: [], fallers: [] } from the comparison block. */
  movers: {
    type: Object,
    default: () => ({ risers: [], fallers: [] }),
  },
  /** Product lines, for the category labels. */
  productLines: {
    type: Array,
    default: () => [],
  },
});

/** Collapsed by default: past about six rows a column stops being scannable. */
const VISIBLE = 6;

const expanded = ref({ risers: false, fallers: false });
const groupByCategory = ref(false);

const lineLabels = computed(() => {
  const map = {};
  props.productLines.forEach(line => { map[line.key] = line.label; });
  return map;
});

const allRisers = computed(() => props.movers?.risers ?? []);
const allFallers = computed(() => props.movers?.fallers ?? []);

const risers = computed(() => (expanded.value.risers ? allRisers.value : allRisers.value.slice(0, VISIBLE)));
const fallers = computed(() => (expanded.value.fallers ? allFallers.value : allFallers.value.slice(0, VISIBLE)));

/**
 * Same rows, bucketed by product line, for when "what is shrinking" is really "which part of the
 * business is shrinking".
 * @param {object[]} rows Mover rows.
 * @returns {object[]}
 */
function group(rows) {
  const buckets = new Map();
  rows.forEach(row => {
    const key = row.product_line;
    if (!buckets.has(key)) {
      buckets.set(key, { key, label: lineLabels.value[key] ?? key, total: 0, rows: [] });
    }
    const bucket = buckets.get(key);
    bucket.rows.push(row);
    bucket.total += row.revenue_change;
  });
  return [...buckets.values()].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
}

const groupedRisers = computed(() => group(allRisers.value));
const groupedFallers = computed(() => group(allFallers.value));

const peak = computed(() => Math.max(
  1,
  ...allRisers.value.map(row => Math.abs(row.revenue_change)),
  ...allFallers.value.map(row => Math.abs(row.revenue_change))
));

/**
 * Bar width relative to the biggest move in either direction.
 * @param {object} row Mover row.
 * @returns {string}
 */
function barWidth(row) {
  return `${Math.max(2, (Math.abs(row.revenue_change) / peak.value) * 100)}%`;
}

/**
 * How the change reads. A package with no prior sales has no percent, only a debut.
 * @param {object} row Mover row.
 * @returns {string}
 */
function changeLabel(row) {
  if (row.status === 'new') {
    return 'first sales';
  }
  if (row.status === 'gone') {
    return 'no sales';
  }
  const value = row.revenue_change_percent;
  return value === null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const number = new Intl.NumberFormat('en-US');
</script>

<template>
  <div :class="$style.card">
    <div :class="$style.head">
      <div>
        <h3 :class="$style.title">
          Biggest movers
        </h3>
        <p :class="$style.note">
          Change against the comparison window, ranked by money moved.
        </p>
      </div>
      <label :class="$style.groupToggle">
        <input v-model="groupByCategory" type="checkbox">
        Group by category
      </label>
    </div>

    <div :class="$style.columns">
      <section
        v-for="column in [
          { id: 'risers', title: 'Growing', rows: risers, all: allRisers, grouped: groupedRisers, tone: 'up' },
          { id: 'fallers', title: 'Shrinking', rows: fallers, all: allFallers, grouped: groupedFallers, tone: 'down' },
        ]"
        :key="column.id"
        :class="$style.column"
      >
        <h4 :class="[$style.columnTitle, $style[column.tone]]">
          {{ column.title }}
          <span :class="$style.count">{{ column.all.length }}</span>
        </h4>

        <p v-if="column.all.length === 0" :class="$style.empty">
          Nothing {{ column.tone === 'up' ? 'grew' : 'shrank' }} in this window.
        </p>

        <!-- Grouped: category totals first, packages underneath. -->
        <div v-else-if="groupByCategory" :class="$style.scroll">
          <div v-for="bucket in column.grouped" :key="bucket.key" :class="$style.bucket">
            <div :class="$style.bucketHead">
              <span :class="$style.bucketLabel">{{ bucket.label }}</span>
              <span :class="[$style.delta, $style[column.tone]]">
                {{ bucket.total > 0 ? '+' : '' }}{{ money.format(bucket.total) }}
              </span>
            </div>
            <ul :class="$style.rows">
              <li v-for="row in bucket.rows" :key="row.package_id" :class="$style.compactRow">
                <span :class="$style.name">{{ row.name }}</span>
                <span :class="$style.compactMeta">
                  {{ number.format(row.previous_units) }} → {{ number.format(row.units) }}
                </span>
                <span :class="[$style.compactDelta, $style[column.tone]]">
                  {{ row.revenue_change > 0 ? '+' : '' }}{{ money.format(row.revenue_change) }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Flat: the biggest movers, with bars. -->
        <template v-else>
          <ul :class="[$style.rows, $style.scroll]">
            <li v-for="row in column.rows" :key="row.package_id" :class="$style.row">
              <span :class="$style.rowHead">
                <span :class="$style.name">{{ row.name }}</span>
                <span :class="[$style.delta, $style[column.tone]]">
                  {{ row.revenue_change > 0 ? '+' : '' }}{{ money.format(row.revenue_change) }}
                </span>
              </span>
              <span :class="$style.track">
                <span
                  :class="[$style.bar, column.tone === 'up' ? $style.barUp : $style.barDown]"
                  :style="{ width: barWidth(row) }"
                />
              </span>
              <span :class="$style.rowFoot">
                <span>{{ changeLabel(row) }}</span>
                <span>{{ number.format(row.previous_units) }} → {{ number.format(row.units) }} reg</span>
              </span>
            </li>
          </ul>

          <button
            v-if="column.all.length > column.rows.length || expanded[column.id]"
            type="button"
            :class="$style.more"
            @click="expanded[column.id] = !expanded[column.id]"
          >
            {{ expanded[column.id]
              ? 'Show fewer'
              : `Show all ${column.all.length}` }}
          </button>
        </template>
      </section>
    </div>
  </div>
</template>

<style module>
.card {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.note {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}
.column {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.columnTitle {
  margin: 0 0 10px;
  font-size: var(--font-size-md);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rowHead {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
}
.name {
  font-size: var(--font-size-lg);
  color: var(--primary-font-dark-color);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.delta {
  font-size: var(--font-size-lg);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.track {
  display: block;
  height: 5px;
  border-radius: 3px;
  background: var(--background-color);
  overflow: hidden;
}
.bar {
  display: block;
  height: 100%;
  border-radius: 3px;
}
.barUp { background: var(--green-light); }
.barDown { background: var(--button-red); }
.rowFoot {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: var(--font-size-md);
  color: var(--faint-color);
  font-variant-numeric: tabular-nums;
}
.up { color: var(--green-light); }
.down { color: var(--button-red); }
.empty {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
}
.groupToggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-md);
  color: var(--primary-font-color);
  cursor: pointer;
  white-space: nowrap;
}
.count {
  font-size: var(--font-size-md);
  font-weight: 400;
  color: var(--faint-color);
  text-transform: none;
  letter-spacing: 0;
  margin-left: 4px;
}
/* A hard ceiling, so an unusually long tail can never push the rest of the page away. */
.scroll {
  max-height: 340px;
  overflow-y: auto;
}
.bucket {
  margin-bottom: 12px;
}
.bucketHead {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--border-color-light);
  margin-bottom: 5px;
}
.bucketLabel {
  font-size: var(--font-size-md);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--faint-color);
}
.compactRow {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 10px;
  align-items: baseline;
  font-size: var(--font-size-md);
  padding: 2px 0;
}
.compactMeta {
  color: var(--faint-color);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.compactDelta {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  white-space: nowrap;
}
.more {
  align-self: flex-start;
  margin-top: 10px;
  border: none;
  background: none;
  color: var(--pfs-teal);
  font: inherit;
  font-size: var(--font-size-md);
  text-decoration: underline;
  cursor: pointer;
}
</style>
